import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { api } from '../../lib/api.js';
import { Page } from '../../app/AppShell.js';
import { relativeTime, absoluteTime } from '../../lib/format.js';
import { Loading, ErrorNote, EmptyState } from '../../components/ui.js';
import type { AuditEntry } from '../../lib/types.js';

export function AuditLogScreen(): ReactNode {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>();
  const [action, setAction] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  function load(reset: boolean): void {
    setLoading(true);
    const params: Record<string, string> = {};
    if (action) params.action = action;
    if (!reset && cursor) params.cursor = cursor;
    api
      .auditLogs(params)
      .then((page) => {
        setEntries((prev) => (reset ? page.data : [...prev, ...page.data]));
        setCursor(page.nextCursor);
        setError(undefined);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err : new Error(String(err))))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load(true);
  }, [action]);

  return (
    <Page title="Audit log">
      <div className="row" style={{ marginBottom: 12 }}>
        <input
          placeholder="Filter by action, e.g. layout.published"
          value={action}
          onChange={(e) => setAction(e.target.value.trim())}
          style={{ maxWidth: 320 }}
        />
      </div>

      {loading && entries.length === 0 ? <Loading what="audit log" /> : null}
      {error ? <ErrorNote error={error} /> : null}
      {!loading && entries.length === 0 && !error ? <EmptyState>Nothing recorded yet.</EmptyState> : null}

      {entries.length > 0 ? (
        <div className="card">
          <table className="grid">
            <thead>
              <tr>
                <th>When</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Target</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <Fragment key={e.id}>
                  <tr
                    className="clickable"
                    onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                  >
                    <td className="muted small" title={absoluteTime(e.at)}>
                      {relativeTime(e.at)}
                    </td>
                    <td className="small">{e.actorLabel ?? e.actorType}</td>
                    <td className="mono small">{e.action}</td>
                    <td className="muted small">
                      {e.targetLabel ?? (e.targetType ? `${e.targetType} ${e.targetId ?? ''}` : '—')}
                    </td>
                    <td className="faint small">{e.changes ? '▾' : ''}</td>
                  </tr>
                  {expanded === e.id ? (
                    <tr>
                      <td colSpan={5}>
                        <div className="stack small" style={{ padding: '4px 2px' }}>
                          {e.summary ? <div>{e.summary}</div> : null}
                          {e.changes ? (
                            <table className="grid">
                              <tbody>
                                {Object.entries(e.changes).map(([field, value]) => (
                                  <tr key={field}>
                                    <td className="mono small">{field}</td>
                                    <td className="mono small">
                                      {Array.isArray(value)
                                        ? `${JSON.stringify(value[0])} → ${JSON.stringify(value[1])}`
                                        : JSON.stringify(value)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : null}
                          <div className="faint">
                            {e.ip ?? '—'} · req {e.requestId ?? '—'}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {cursor ? (
        <div className="row" style={{ marginTop: 12 }}>
          <button className="btn small" disabled={loading} onClick={() => load(false)}>
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      ) : null}
    </Page>
  );
}
